import React from 'react';

/**
 * Render text content with clickable links (no HTML allowed, just URL detection)
 *
 * Detects two types of URLs:
 * 1. Full URLs starting with http:// or https://
 * 2. Bare domains like x.com/path or example.com
 *
 * Preserves whitespace before bare domains (fixes "also x.com" rendering as "alsox.com")
 */
export function renderContentWithLinks(content: string): React.ReactNode {
  // Two separate patterns to handle full URLs and bare domains properly
  const fullUrlPattern = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;
  // Bare domain pattern - captures leading space/start separately from the URL
  const bareDomainPattern =
    /(?:^|[\s])((?:[a-zA-Z0-9][-a-zA-Z0-9]*\.)+[a-zA-Z]{2,}(?:\/[^\s<>"{}|\\^`[\]]*)?)/g;

  interface UrlMatch {
    start: number;
    end: number;
    url: string;
  }

  const matches: UrlMatch[] = [];

  // Find full URLs
  let match;
  fullUrlPattern.lastIndex = 0;
  while ((match = fullUrlPattern.exec(content)) !== null) {
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      url: match[0],
    });
  }

  // Find bare domains (group 1 is the actual domain, group 0 may include leading space)
  bareDomainPattern.lastIndex = 0;
  while ((match = bareDomainPattern.exec(content)) !== null) {
    const actualUrl = match[1];
    const startOffset = match[0].length - actualUrl.length; // Space before URL (if any)
    matches.push({
      start: match.index + startOffset,
      end: match.index + match[0].length,
      url: actualUrl,
    });
  }

  // Sort by position and filter overlapping matches (full URLs take precedence)
  matches.sort((a, b) => a.start - b.start);

  const filteredMatches: UrlMatch[] = [];
  for (const m of matches) {
    const lastMatch = filteredMatches[filteredMatches.length - 1];
    if (!lastMatch || m.start >= lastMatch.end) {
      filteredMatches.push(m);
    }
  }

  // Build the parts
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const m of filteredMatches) {
    // Add text before this match (preserves spaces)
    if (m.start > lastIndex) {
      parts.push(content.slice(lastIndex, m.start));
    }

    // Add the link
    const href = m.url.startsWith('http') ? m.url : `https://${m.url}`;
    parts.push(
      <a
        key={m.start}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-600 hover:text-brand-700 underline"
      >
        {m.url}
      </a>
    );

    lastIndex = m.end;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts.length > 0 ? parts : content;
}
